import React, { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Heading,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@moonpig/launchpad-components'
import { TextInput } from '@moonpig/launchpad-forms'
import {
  LocaleTextDictionary,
  LocaleTextHook,
} from '@moonpig/web-core-locale-text'
import { useFormContext } from 'react-hook-form'
import { useAuthFetch } from '@moonpig/web-core-auth'
import { useStore } from '@moonpig/web-core-stores'
import { useAuthenticationLocaleText } from '../../../../text-localisation'
import { authenticationService } from '../../AuthenticationService'
import { LoadingOverlay } from '../../../shared/LoadingOverlay'
import { LoginResult } from '../../types'

const formatTimer = (timer: number) => {
  return `${String(Math.floor(timer / 60)).padStart(2, '0')}:${String(
    timer % 60,
  ).padStart(2, '0')}`
}

export const ANALYTICS_MESSAGES = {
  sendCodeErrors: {
    unknownError: 'unspecified error sending code',
  },
  loginWithCodeErrors: {
    accountDisabled: 'account disabled',
    invalidCode: 'invalid code',
    expiredCode: 'expired code',
    unknownError: 'unspecified error logging in with code',
  },
}

const timeToResendCode = 30

const ResendCodeMessage: FC<{
  timer: number
  setTimer: (timer: number) => void
  localiseText: LocaleTextHook<LocaleTextDictionary<string>>
  resendLoginCodeHelper: () => void
  accountLocked: boolean
  loading: boolean
}> = ({
  timer,
  setTimer,
  localiseText,
  resendLoginCodeHelper,
  accountLocked,
  loading,
}) => {
  if (accountLocked) {
    return <></>
  }

  if (timer > 0) {
    return (
      <>
        <Text>
          {localiseText('sign_in_with_code_form.resend_code_countdown')}{' '}
        </Text>
        <Text data-testid="countdown">{formatTimer(timer)}</Text>
      </>
    )
  }

  return (
    <>
      <Text>
        {localiseText('sign_in_with_code_form.resend_code')}&nbsp;
        <TertiaryButton
          inline
          onClick={() => {
            setTimer(timeToResendCode)
            resendLoginCodeHelper()
          }}
          style={{ border: 0 }}
          disabled={loading}
        >
          {localiseText('sign_in_with_code_form.resend_code_link')}
        </TertiaryButton>
      </Text>
    </>
  )
}

export type SignInWithCodeFormProps = {
  email: string
  flowId?: string
  returnUrl?: string
  submitButtonText?: string
  onSubmitCallback?: () => void
  onLoginSuccessCallback?: () => void
  onLoginErrorCallback?: (errorMessage: string) => void
  onResendCodeCallback?: () => void
  onResendCodeErrorCallback?: (errorMessage: string) => void
}

type error = {
  title?: string
  message?: string
  accountLocked?: boolean
}

export const SignInWithCodeForm: FC<SignInWithCodeFormProps> = props => {
  const localiseText = useAuthenticationLocaleText()
  const [error, setError] = useState<error | undefined>(undefined)
  const [timer, setTimer] = useState(timeToResendCode)
  const [flowId, setFlowId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const { register, watch } = useFormContext()
  const loginCode = watch('loginCode')
  const authFetch = useAuthFetch()
  const {
    store: { id: region },
  } = useStore()
  const { sendLoginCode, loginWithCode } = authenticationService(
    authFetch,
    region,
  )

  const sendLoginCodeHelper = () => {
    setLoading(true)
    sendLoginCode({ email: props.email, flowId: props.flowId }).then(result => {
      if (result.flowId) {
        setFlowId(result.flowId)
        setError(undefined)
      } else {
        if (props.onResendCodeErrorCallback) {
          props.onResendCodeErrorCallback(
            ANALYTICS_MESSAGES.sendCodeErrors.unknownError,
          )
        }

        setError({})
        setTimer(0)
      }
      setLoading(false)
    })
  }

  const resendLoginCodeHelper = () => {
    if (props.onResendCodeCallback) props.onResendCodeCallback()
    sendLoginCodeHelper()
  }

  const errorMessage = (result: LoginResult) => {
    if (result.accountLocked)
      return ANALYTICS_MESSAGES.loginWithCodeErrors.accountDisabled
    if (result.errorCode === 'invalid')
      return ANALYTICS_MESSAGES.loginWithCodeErrors.invalidCode
    if (result.errorCode === 'expired')
      return ANALYTICS_MESSAGES.loginWithCodeErrors.expiredCode
    return ANALYTICS_MESSAGES.loginWithCodeErrors.unknownError
  }

  const loginWithCodeHelper = () => {
    if (props.onSubmitCallback) props.onSubmitCallback()
    setLoading(true)
    loginWithCode({
      flowId,
      email: props.email,
      code: loginCode,
    }).then(result => {
      if (!result.success) {
        if (props.onLoginErrorCallback)
          props.onLoginErrorCallback(errorMessage(result))
        setError({
          title: result.errorTitle,
          message: result.errorMessage,
          accountLocked: result.accountLocked,
        })
        setLoading(false)
      } else if (props.onLoginSuccessCallback) props.onLoginSuccessCallback()
    })
  }

  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer(prevTimer => prevTimer - 1)
      }, 1000)

      return () => clearInterval(intervalId)
    }
  }, [timer])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(sendLoginCodeHelper, [props.email])

  const isLocked = error?.accountLocked ?? false
  const validCode = loginCode && loginCode.length === 6
  const canContinue = !!flowId && !!validCode && !isLocked && !loading

  return (
    <Box>
      <TextInput
        label={localiseText('sign_in_with_code_form.code_label')}
        value={loginCode}
        placeholder={localiseText('sign_in_with_code_form.code_placeholder')}
        maxLength={6}
        mb={6}
        autoComplete="one-time-code"
        {...register('loginCode')}
      />
      {error && (
        <Alert
          variant="error"
          mb={6}
          aria-describedby={'sign-in-with-code-form-error'}
        >
          <Heading
            level="h5"
            mt={2}
            mb={2}
            typography={'typeBodyLabel'}
            color={'colorTextBody'}
          >
            {error.title ||
              localiseText('sign_in_with_code_form.error_heading')}
          </Heading>
          <Text typography={'typeBodyText'}>
            {error.message ||
              localiseText('sign_in_with_code_form.error_message')}
          </Text>
        </Alert>
      )}

      <PrimaryButton
        mb={6}
        disabled={!canContinue}
        width="100%"
        onClick={loginWithCodeHelper}
      >
        {props.submitButtonText ??
          localiseText('sign_in_with_code_form.submit_button_text')}
      </PrimaryButton>
      <ResendCodeMessage
        timer={timer}
        setTimer={setTimer}
        localiseText={localiseText}
        resendLoginCodeHelper={resendLoginCodeHelper}
        accountLocked={isLocked}
        loading={loading}
      />
      {loading && (
        <LoadingOverlay label={localiseText('auth_wizard.common.loading')} />
      )}
    </Box>
  )
}
